import * as React from "react";
import * as _ from "lodash";
import { Dispatch, connect } from "react-redux";
import {
    IAppState, IApplicationState, AppActionCreators, ICrmState, CrmActionCreators, ICrmAccountPlanResponse,
    ICrmLookupDataResponse, ISaveProductCenterResponse, ICenterState, CenterActionCreators
} from "@nkr/store/index";
import { IProductCenter, IBaseResponse, IContact } from '@nkr/models/index';
import { Button, Panel, Glyphicon, FormControl } from "react-bootstrap";
import { toast } from "react-toastify";
import Product from "./product";
import AddProduct from "./add-product";
import DeleteProduct from './delete-product';
import { ProductWrapper } from "./styles";

interface IProductCenterForState extends IProductCenter {
    dirty: boolean
}

interface IProps {
    centerId: number
}

interface IStateProps {
    crm: ICrmState,
    centers: ICenterState,
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getLookupData: () => Promise<ICrmLookupDataResponse>,
    getAccountPlan: (centerId: number) => Promise<ICrmAccountPlanResponse>,
    saveProductCenter: (centerId: number, productId: number, nextSteps: string, targetDate: string, closedDate: string, closedBy: number) => Promise<IBaseResponse>,
    saveProductCenters: (requests: { centerId: number, productId: number, nextSteps: string, targetDate: string, closeDate: string, closedBy: number }[]) => Promise<IBaseResponse>,
    deleteProduct: (centerId: number, productId: number) => Promise<IBaseResponse>,
    saveCenterNotes: (centerId: number, notes: string) => Promise<IBaseResponse>,
    updateCenterNotes: (notes: string) => void
}

type TProps = IStateProps & IDispatchProps & IProps;

interface IState {
    targetedProducts: IProductCenterForState[],
    expandClosed: boolean,
    delete: {
        show: boolean,
        data: IProductCenter
    },
    notes: string
}

class AccountPlan extends React.Component<TProps, IState> {
    readonly state: IState = {
        targetedProducts: [],
        expandClosed: false,
        delete: {
            show: false,
            data: null
        },
        notes: ''
    }

    componentDidMount = () => {
        this.getData();
    }

    handleExpand = () => {
        this.setState({
            expandClosed: !this.state.expandClosed
        });
    }

    handleAddProduct = (productId: number, nextSteps: string, targetDate: string) => {
        this.props.showLoading('Adding product, one moment please...').then(() => {
            this.props.saveProductCenter(this.props.centerId, productId, nextSteps, targetDate, null, 0)
                .then(response => {
                    if (response.data.success) {
                        this.props.getAccountPlan(this.props.centerId).then(accountPlanResponse => {
                            this.setState({
                                targetedProducts: accountPlanResponse.data.results.targetedProducts.map(pc => {
                                    return { ...pc, dirty: false };
                                })
                            }, () => {
                                toast.success('Next steps updated successfully');
                            });
                        });
                    } else {
                        toast.error('Unable to update next steps')
                    }

                    this.props.hideLoading();
                })
                .catch(error => {
                    toast.error('Unable to update next steps');
                    this.props.hideLoading();
                });
        });
    }

    handleConfirmDeleteProduct = (productId: number) => {
        let pc = this.props.crm.accountPlan.targetedProducts.find(x => x.productId == productId);

        this.setState({
            delete: {
                show: true,
                data: pc
            }
        });
    }

    handleCancelDeleteProduct = () => {
        this.setState({
            delete: {
                show: false,
                data: null
            }
        });
    }

    handleDeleteProduct = () => {
        let productId = this.state.delete.data.productId;

        this.setState({
            delete: {
                show: false,
                data: null
            }
        }, () => {
            this.props.showLoading('Deleting targeted product, one moment please...').then(() => {
                this.props.deleteProduct(this.props.centerId, productId)
                    .then(response => {
                        this.props.hideLoading();

                        if (response.data.success) {
                            this.props.getAccountPlan(this.props.centerId).then(accountPlanResponse => {
                                this.setState({
                                    targetedProducts: accountPlanResponse.data.results.targetedProducts.map(pc => {
                                        return { ...pc, dirty: false };
                                    })
                                }, () => {
                                    toast.success('Successfully delete the target product');
                                });
                            });
                        } else {
                            toast.error('Unable to delete the targeted product');
                        }
                    })
                    .catch(error => {
                        toast.error('Unable to delete the targeted product');
                        this.props.hideLoading();
                    });
            });
        });
    }

    handleNextStepsChange = (productId: number, nextSteps: string) => {
        let targetedProducts = this.state.targetedProducts.map(pc => {
            if (pc.productId == productId) {
                pc.nextSteps = nextSteps;
                pc.dirty = true;
            }

            return pc;
        });

        this.setState({
            targetedProducts
        });
    }

    handleTargetDateChange = (productId: number, targetDate: string) => {
        let targetedProducts = this.state.targetedProducts.map(pc => {
            if (pc.productId == productId) {
                pc.targetDate = targetDate;
                pc.dirty = true;
            }

            return pc;
        });

        this.setState({
            targetedProducts
        });
    }

    handleCloseDateChange = (productId: number, closeDate: string) => {
        let targetedProducts = this.state.targetedProducts.map(pc => {
            if (pc.productId == productId) {
                pc.closeDate = closeDate;
                pc.dirty = true;
            }

            return pc;
        });

        this.setState({
            targetedProducts
        });
    }

    handleClosedByChange = (productId: number, closedBy: IContact) => {
        let targetedProducts = this.state.targetedProducts.map(pc => {
            if (pc.productId == productId) {
                pc.closedBy = closedBy;
                pc.dirty = true;
            }

            return pc;
        });

        this.setState({
            targetedProducts
        });
    }

    handleSaveTargetedProducts = () => {
        let centerId = this.props.centerId;

        let requests = this.state.targetedProducts
            .filter(x => x.dirty)
            .map(pc => {
                return {
                    centerId,
                    productId: pc.productId,
                    nextSteps: pc.nextSteps,
                    targetDate: pc.targetDate,
                    closeDate: pc.closeDate,
                    closedBy: pc.closedBy != null ? pc.closedBy.id : 0
                };
            });

        this.props.showLoading('Saving targeted product changes, one moment please...').then(() => {
            this.props.saveProductCenters(requests)
                .then(response => {
                    if (response.data.success) {
                        this.props.getAccountPlan(this.props.centerId).then(response => {
                            if (response.data.success) {
                                this.setState({
                                    targetedProducts: response.data.results.targetedProducts.map(pc => {
                                        return { ...pc, dirty: false };
                                    })
                                }, () => {
                                    toast.success('Targeted products updated successfully');
                                });
                            }
                        });
                    } else {
                        toast.error('Unable to update targeted products')
                    }

                    this.props.hideLoading();
                })
                .catch(error => {
                    toast.error('Unable to update targeted products');
                    this.props.hideLoading();
                });
        });
    }

    handleNotesChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        this.setState({
            notes: e.currentTarget.value
        });
    }

    handleSaveNotes = () => {
        this.props.showLoading('Saving center notes, one moment please...').then(() => {
            this.props.saveCenterNotes(this.props.centerId, this.state.notes)
                .then(response => {
                    if (response.data.success) {
                        this.props.updateCenterNotes(this.state.notes);
                        toast.success('Center notes saved successfully');
                    } else {
                        toast.error('Unable to save the center notes')
                    }

                    this.props.hideLoading();
                })
                .catch(error => {
                    toast.error('Unable to save the center notes');
                    this.props.hideLoading();
                });
        });
    }

    getData = () => {
        this.props.showLoading("Getting account plan, one moment please...").then(() => {
            Promise.all([
                this.props.getLookupData(),
                this.props.getAccountPlan(this.props.centerId)
            ]).then(response => {
                let accountPlanResponse = response[1];
                let { success, results } = accountPlanResponse.data;

                if (success) {
                    this.setState({
                        targetedProducts: results.targetedProducts.map(pc => {
                            return { ...pc, dirty: false };
                        }),
                        notes: results.notes
                    });
                }

                this.props.hideLoading();
            });
        });
    }

    render = () => {
        if (this.props.crm.accountPlan == null)
            return null;

        const { center, closedProducts } = this.props.crm.accountPlan;
        const disableSaveNotesButton = this.state.notes == this.props.crm.accountPlan.notes;
        const disableSaveButton = this.state.targetedProducts.filter(x => x.dirty).length == 0;

        return (
            <>
                <div style={{ marginBottom: 20, display: "flex", justifyContent: 'space-between', alignItems: "center" }}>
                    <div style={{ fontWeight: 500, fontSize: 28 }}>Account Plan for {center.alias}</div>
                    <div style={{ marginRight: 10 }}><a href="/admin/centers">Manage Centers</a></div>
                </div>

                <Panel>
                    <Panel.Heading>
                        <Panel.Title>Center Notes</Panel.Title>
                    </Panel.Heading>
                    <Panel.Body>
                        <FormControl
                            componentClass='textarea'
                            style={{ height: 100 }}
                            value={this.state.notes}
                            onChange={this.handleNotesChange}
                        />
                    </Panel.Body>
                    <Panel.Footer>
                        <Button bsStyle="primary" disabled={disableSaveNotesButton} onClick={this.handleSaveNotes}>Save Changes</Button>
                    </Panel.Footer>
                </Panel>

                <Panel>
                    <Panel.Heading>
                        <Panel.Title>Currently Targeted Products</Panel.Title>
                    </Panel.Heading>
                    <Panel.Body>
                        <ProductWrapper>
                            {this.state.targetedProducts.map((pc, index) =>
                                <Product
                                    key={index}
                                    productCenter={pc}
                                    staffList={this.props.crm.staffList}
                                    isClosed={false}
                                    onNextStepsChange={this.handleNextStepsChange}
                                    onTargetDateChange={this.handleTargetDateChange}
                                    onCloseDateChange={this.handleCloseDateChange}
                                    onClosedByChange={this.handleClosedByChange}
                                    onDeleteProduct={this.handleConfirmDeleteProduct}
                                />
                            )}
                        </ProductWrapper>

                        {this.state.targetedProducts.length == 0 && (
                            <div style={{ marginBottom: 10 }}>There are no actively targeted products...</div>
                        )}
                    </Panel.Body>
                    <Panel.Footer>
                        <Button bsStyle="primary" disabled={disableSaveButton} onClick={this.handleSaveTargetedProducts}>Save Changes</Button>
                    </Panel.Footer>
                </Panel>

                <AddProduct
                    productList={this.props.crm.accountPlan.availableProducts}
                    staffList={this.props.crm.staffList}
                    nkrLiaisonId={this.props.crm.accountPlan.center.nkrLiaisonId}
                    nkrExecutiveSponsorId={this.props.crm.accountPlan.center.executiveSponsorId}
                    onAddProduct={this.handleAddProduct}
                />

                <Panel>
                    <Panel.Heading style={{ display: 'flex' }}>
                        <Panel.Title toggle style={{ marginRight: 10 }} onClick={this.handleExpand}>Closed Products</Panel.Title>
                        <Panel.Toggle componentClass='div' style={{ display: 'flex', alignItems: 'center' }} onClick={this.handleExpand}><Glyphicon glyph={this.state.expandClosed ? 'triangle-bottom' : 'triangle-top'} /></Panel.Toggle>
                    </Panel.Heading>
                    <Panel.Collapse>
                        <Panel.Body>
                            <ProductWrapper>
                                {closedProducts.map((pc, index) =>
                                    <Product
                                        key={index}
                                        productCenter={pc}
                                        staffList={[]}
                                        isClosed={true}
                                        onNextStepsChange={() => null}
                                        onTargetDateChange={() => null}
                                        onCloseDateChange={() => null}
                                        onClosedByChange={() => null}
                                        onDeleteProduct={() => null}
                                    />
                                )}
                            </ProductWrapper>

                            {closedProducts.length == 0 && (
                                <div>There are no closed products...</div>
                            )}
                        </Panel.Body>
                    </Panel.Collapse>
                </Panel>

                <DeleteProduct
                    show={this.state.delete.show}
                    product={this.state.delete.data != null ? this.state.delete.data.productName : ''}
                    onDelete={this.handleDeleteProduct}
                    onCancel={this.handleCancelDeleteProduct}
                />
            </>
        )
    }
}

const mapStateToProps = (appState: IApplicationState, ownProps: IProps): IStateProps => {
    return {
        crm: appState.crm,
        centers: appState.centers,
        app: appState.app
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => { return dispatch(AppActionCreators.showLoadingAsync(message)); },
        hideLoading: () => { dispatch(AppActionCreators.hideLoading()); },
        getLookupData: () => { return dispatch(CrmActionCreators.getCrmLookupData()); },
        getAccountPlan: (centerId) => { return dispatch(CrmActionCreators.getAccountPlan(centerId)); },
        saveProductCenter: (centerId, productId, nextSteps, targetDate, closedDate, closedBy) => { return dispatch(CrmActionCreators.saveProductCenter(centerId, productId, nextSteps, targetDate, closedDate, closedBy)); },
        saveProductCenters: (requests) => { return dispatch(CrmActionCreators.saveProductCenters(requests)); },
        deleteProduct: (centerId, productId) => { return dispatch(CrmActionCreators.deleteProductCenter(centerId, productId)); },
        saveCenterNotes: (centerId, notes) => { return dispatch(CenterActionCreators.saveCenterNotes(centerId, notes)); },
        updateCenterNotes: (notes) => { return dispatch(CrmActionCreators.updateCenterNotes(notes)); }
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(AccountPlan);